import { Box, InsuranceIcon } from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { setSignalMemberFilter } from '../../../../services/map-layer/image-layers/signal-markers';
import { AppState } from '../../../../services/store';
import {
  fetchSignal,
  getAllSignalsSharedWithCurrentMember,
  getSignal,
  setSelectedSignal,
} from '../../../../services/store/modules/signal';
import GroupDetailView, {
  GroupDetailMember,
} from '../components/GroupDetailView/GroupDetailView';
import {
  formatDistanceFromMember,
  sortByOnWaterThenDistance,
} from '../helpers/signal-sorting';
import useSignalUtilities from '../hooks/use-signal-utilities';

const SIGNAL_QUERY_PARAM = 'signal';

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const SafetyContactsPage: FC = () => {
  const { pathname, search } = useLocation();
  const safetyContacts = useSelector(getAllSignalsSharedWithCurrentMember);
  const { centerMapOnSignal } = useSignalUtilities();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectedSignalId =
    new URLSearchParams(search).get(SIGNAL_QUERY_PARAM) || undefined;

  const selectedSignal = useSelector((state: AppState) =>
    selectedSignalId ? getSignal(state, selectedSignalId) : undefined,
  );

  useEffect(() => {
    if (!selectedSignalId) {
      dispatch(setSelectedSignal(undefined));
      return;
    }
    dispatch(setSelectedSignal(selectedSignalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(selectedSignalId));
        await centerMapOnSignal(selectedSignalId);
      } catch {
        await centerMapOnSignal(selectedSignalId);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSignalId]);

  // Create stable member IDs string for dependency comparison
  const safetyContactMemberIds = safetyContacts.map(
    (signal) => signal.memberid,
  );
  const memberIdsKey = safetyContactMemberIds.join(',');

  // Filter map signals to only show safety contacts
  useEffect(() => {
    setSignalMemberFilter(safetyContactMemberIds);

    // Clear filter when component unmounts
    return () => {
      setSignalMemberFilter(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIdsKey]);

  const handleBack = () => {
    captureEvent({
      type: 'signal: back to groups list from safety contacts',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps/signal/groups');
  };

  const handleClose = () => {
    captureEvent({
      type: 'signal: close safety contacts page',
      payload: {},
      sendToAmplitude: true,
    });
    history.push('/maps');
  };

  const handleMemberClick = (signalId: string) => {
    captureEvent({
      type: 'signal: navigate to safety contact signal',
      payload: {
        signalId,
      },
      sendToAmplitude: true,
    });
    if (!signalId) return;
    const isSameMember = selectedSignalId === signalId;
    if (isSameMember) {
      history.replace(pathname);
      dispatch(setSelectedSignal(undefined));
      return;
    }
    const params = new URLSearchParams(search);
    params.set(SIGNAL_QUERY_PARAM, signalId);
    history.replace(`${pathname}?${params.toString()}`);
    dispatch(setSelectedSignal(signalId));
    const run = async () => {
      try {
        await dispatch(fetchSignal(signalId));
        await centerMapOnSignal(signalId);
      } catch {
        await centerMapOnSignal(signalId);
      }
    };
    run();
  };

  // Transform safety contacts data for the presentation component
  const members: GroupDetailMember[] = sortByOnWaterThenDistance(
    safetyContacts,
  ).map((signal) => ({
    id: signal.id,
    memberId: signal.memberid,
    username: signal.username,
    signalId: signal.id,
    signalImage: signal.image || undefined,
    signalTimestamp: signal.timestamp,
    signalSubscribed: signal.subscribed,
    distanceFromMember: formatDistanceFromMember(signal.distance_from_member),
  }));

  return (
    <GroupDetailView
      isLoading={false}
      error={false}
      groupName="People you are a safety contact of"
      groupIcon={
        <IconWrapper>
          <InsuranceIcon size={28} />
        </IconWrapper>
      }
      memberCountLabel={`${members.length} member${
        members.length !== 1 ? 's' : ''
      }`}
      members={members}
      selectedSignalId={selectedSignalId}
      selectedSignal={selectedSignal ?? null}
      onBack={handleBack}
      onClose={handleClose}
      onMemberClick={handleMemberClick}
    />
  );
};

export default SafetyContactsPage;
